import PropTypes from 'prop-types';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import utils from './utils';

export const LIST_TYPE_GROUP = 'group';

const useSmartAllocationDragAndDrop = ({
  groupResource,
  optimisticUpdateSacGroup,
  isOptimisticallyUpdating,
  sacGroupsCollection,
}) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const groupConfig = groupResource.attr('steps').resources().list().reduce((acc, curr) => {
    // stepSet defines the SAC step's grouping
    const currStepSet = curr.attr('stepSet');

    if (acc.groupMap[currStepSet]) {
      // If we're already tracking the step grouping, add this step to that group list
      acc.groupMap[currStepSet].push(curr);
    } else {
      // Otherwise, start tracking a new step grouping list
      acc.groupMap[currStepSet] = [curr];
      // Add group ID to list
      acc.groups.push(currStepSet);
    }

    return acc;
  }, { groupMap: {}, groups: [] });

  // Transform groupMap into an array of group lists
  const { groupMap } = groupConfig;
  const groupSteps = groupConfig.groups.reduce((acc, curr) => {
    return [...acc, groupMap[curr]];
  }, []);

  const isGroupActive = (steps) => steps.every(step => step.isTruthy('isActive'));

  // Filter step groups into active & inactive
  const activeGroupSteps = groupSteps.filter(steps => steps.length > 0 && isGroupActive(steps));
  const inactiveGroupSteps = groupSteps.filter(steps => steps.length > 0 && !isGroupActive(steps));

  const getStepSetId = (id) => id.split('.').slice(-1)[0];

  const onDragEnd = ({ source, destination, combine }) => {
    if (isOptimisticallyUpdating) {
      return undefined;
    }

    // Item was dropped into the same place it started - No-Op
    if (combine === null && destination !== null
        && destination.droppableId === source.droppableId
        && destination.index === source.index) {
      return undefined;
    }

    let updatedGroupResource = null;
    if (combine) {
      updatedGroupResource = utils.combineStepSets(
        logStandardizedEvent,
        groupResource,
        activeGroupSteps, inactiveGroupSteps,
        source, combine,
      );
    } else if (destination) {
      updatedGroupResource = utils.moveStepSet(
        logStandardizedEvent,
        groupResource,
        activeGroupSteps, inactiveGroupSteps,
        source, destination,
      );
    } else {
      return undefined;
    }

    if (updatedGroupResource) {
      return optimisticUpdateSacGroup(sacGroupsCollection, updatedGroupResource);
    }

    return undefined;
  };

  const handleBreakGroup = async (stepSet) => {
    if (isOptimisticallyUpdating) {
      return undefined;
    }

    logStandardizedEvent(categories.whatIf, eventTypes.clickButton, 'break up step set');
    const updatedGroupResource = utils.breakStepSetGroup(groupResource, stepSet);
    return optimisticUpdateSacGroup(sacGroupsCollection, updatedGroupResource);
  };

  const isCompatibleDropTarget = (stepResources, draggingId) => {
    // When a drag isn't occurring drag id will be set to null, so just consider this incompatible.
    // The UI will not use this value if not being dragged because it is also taking hints from the "isDraggingOver" bool returned from the library.
    if (!draggingId) {
      return null;
    }

    const draggingStepSet = getStepSetId(draggingId);

    return utils.isCompatibleDropTarget(groupResource, draggingStepSet, stepResources[0].attr('stepSet'));
  };

  return {
    onDragEnd,
    onBreakGroup: handleBreakGroup,
    activeGroupSteps,
    inactiveGroupSteps,
    isNeverDraggable: !groupResource.isTruthy('canBeRanked'),
    isCurrentlyNotDraggable: isOptimisticallyUpdating,
    isCompatibleDropTarget,
  };
};

useSmartAllocationDragAndDrop.propTypes = {
  groupResource: PropTypes.object.isRequired,
};

export default useSmartAllocationDragAndDrop;
